import Flickity from 'flickity';
import FlickityFade from 'flickity-fade';

import debounce from '../util/debounce';

const selectors = {
  collectionImage: '.collection-item__image',
  columnImage: '.column__image__wrapper',
  aos: '[data-aos]',
  flickityNextArrow: '.flickity-button.next',
  flickityPrevArrow: '.flickity-button.previous',
  heroContent: '.hero__content',
  heroContentWrapper: '.hero__content__wrapper',
  link: 'a:not(.hero__btn)',
  nextArrow: '[data-next-arrow]',
  prevArrow: '[data-prev-arrow]',
  productItemImage: '.product-item__image',
  slide: '[data-slide]',
  slideValue: 'data-slide',
  slider: '[data-slider]',
  sliderThumb: '[data-slider-thumb]',
  sliderThumbClick: '[data-slider-thumb-click]',
  slideshowSlideImg: '.slide-image-img',
};

const attributes = {
  sliderOptions: 'data-options',
  arrowPositionMiddle: 'data-arrow-position-middle',
  aspectRatio: 'data-aspectratio',
  equalizeHeight: 'data-equalize-height',
  setHeight: 'data-set-height',
  slideIndex: 'data-slide-index',
  sliderAnimate: 'data-slider-animate',
  sliderAnimateOnce: 'data-slider-animate-once',
  slidesDesktop: 'data-slides-desktop',
  slidesLargeDesktop: 'data-slides-large-desktop',
  slidesMobileDesktop: 'data-slides-mobile',
  slidesTabletDesktop: 'data-slides-tablet',
  slideTextColor: 'data-slide-text-color',
};

const classes = {
  aosAnimated: 'aos-animated',
  flickityResize: 'flickity-resize',
  heroContentTransparent: 'hero__content--transparent',
  isSelected: 'is-selected',
  sliderArrowsHidden: 'flickity-button-hide',
  sliderInitialized: 'js-slider--initialized',
  aosAnimate: 'aos-animate',
};

const sections = {};

class Slider {
  constructor(container, slideshow = null) {
    this.container = container;
    this.slideshow = slideshow || this.container.querySelector(selectors.slider);

    if (!this.slideshow) return;

    this.slideshowSlides = this.slideshow.querySelectorAll(selectors.slide);
    this.sliderPrev = this.container.querySelector(selectors.prevArrow);
    this.sliderNext = this.container.querySelector(selectors.nextArrow);
    this.sliderThumbs = this.container.querySelectorAll(selectors.sliderThumb);
    this.sliderThumbsClick = this.container.querySelectorAll(selectors.sliderThumbClick);
    this.multipleSlides = this.slideshow.hasAttribute(attributes.slidesLargeDesktop);
    this.dataEqualizeHeight = this.slideshow.getAttribute(attributes.equalizeHeight) === 'true';
    this.sliderAnimate = this.slideshow.getAttribute(attributes.sliderAnimate) === 'true';
    this.sliderAnimateOnce = this.slideshow.getAttribute(attributes.sliderAnimateOnce) === 'true';
    this.setMinHeightFlag = this.slideshow.getAttribute(attributes.setHeight) === 'true';

    this.resizeEvent = debounce(() => this.resizeEvents(), 100);
    this.resizeEventAlt = debounce(() => this.addRemoveSlidesForDevices(), 100);
    this.resetSliderEvent = () => this.resetSlider();

    if (this.slideshow.hasAttribute(attributes.sliderOptions)) {
      this.customOptions = JSON.parse(decodeURIComponent(this.slideshow.getAttribute(attributes.sliderOptions)));
    }

    this.flkty = null;

    this.init();
  }

  init() {
    this.setMinHeight();

    this.sliderOptions = {
      contain: true,
      wrapAround: true,
      adaptiveHeight: true,
      ...this.customOptions,
      on: {
        ready: () => {
          if (this.sliderAnimate && this.sliderAnimateOnce && Boolean(this.sliderOptions.autoPlay)) {
            const currentSlide = this.slideshow.querySelector(`.${classes.isSelected}`);
            currentSlide.classList.add(classes.aosAnimated);
          }

          setTimeout(() => {
            this.slideshow.parentNode.dispatchEvent(
              new CustomEvent('theme:slider:loaded', {
                bubbles: true,
                detail: {
                  slider: this,
                },
              })
            );
          }, 10);

          this.slideActions();

          if (this.slideshow.classList.contains(classes.isSelected)) {
            this.slideshow.classList.remove(classes.isSelected);
          }
          if (this.sliderOptions.prevNextButtons) {
            this.initArrows();
            this.positionArrows();
          }
        },
        resize: () => {
          if (this.sliderOptions.prevNextButtons) {
            this.positionArrows();
          }
        },
      },
    };

    if (this.sliderOptions.fade) {
      this.flkty = new FlickityFade(this.slideshow, this.sliderOptions);
    }

    if (!this.sliderOptions.fade) {
      this.flkty = new Flickity(this.slideshow, this.sliderOptions);
    }

    if (this.dataEqualizeHeight) {
      this.equalizeHeight();
    }

    if (this.sliderPrev) {
      this.sliderPrev.addEventListener('click', (e) => {
        e.preventDefault();

        this.flkty.previous(true);
      });
    }

    if (this.sliderNext) {
      this.sliderNext.addEventListener('click', (e) => {
        e.preventDefault();

        this.flkty.next(true);
      });
    }

    this.flkty.on('change', () => this.slideActions(true));

    this.addRemoveSlidesForDevices();

    window.addEventListener('resize', this.resizeEventAlt);

    if (this.sliderAnimate) {
      this.flkty.on('settle', () => this.sliderSettle());
    }

    if (this.setMinHeightFlag || this.multipleSlides) {
      window.addEventListener('resize', this.resizeEvent);
    }

    if (this.sliderThumbsClick.length) {
      this.sliderThumbsClick.forEach((element) => {
        element.addEventListener('click', (e) => {
          e.preventDefault();
          const slideIndex = [...element.parentElement.children].indexOf(element);
          this.flkty.select(slideIndex);
        });
      });
    }

    this.container.addEventListener('theme:tab:change', this.resetSliderEvent);
  }

  sliderSettle() {
    let animatedItems = this.slideshow.querySelectorAll(`.${classes.isSelected} ${selectors.aos}`);

    if (this.sliderAnimateOnce) {
      animatedItems = this.slideshow.querySelectorAll(`.${classes.isSelected}:not(.${classes.aosAnimated}) .${classes.aosAnimated}`);
    }

    if (animatedItems.length) {
      animatedItems.forEach((animatedItem) => {
        animatedItem.classList.add(classes.aosAnimate);

        if (this.sliderAnimateOnce) {
          animatedItem.closest(`.${classes.isSelected}`).classList.add(classes.aosAnimated);
        }
      });
    }
  }

  // Move slides to their initial position
  resetSlider() {
    if (this.slideshow) {
      if (this.flkty && this.flkty.isActive) {
        this.flkty.select(0, false, true);
      } else {
        this.slideshow.scrollTo({
          left: 0,
          behavior: 'auto',
        });
      }
    }
  }

  addRemoveSlidesForDevices() {
    this.hasDiffSlidesForMobileDesktop =
      Array.prototype.filter.call(this.slideshowSlides, (slide) => {
        if (slide.classList.contains('desktop') || slide.classList.contains('mobile')) {
          return slide;
        }
      }).length > 0;

    if (!this.hasDiffSlidesForMobileDesktop) {
      return;
    }

    const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    let selectorSlides = null;

    if (windowWidth < theme.sizes.small) {
      selectorSlides = `${selectors.slide}.mobile, ${selectors.slide}:not(.desktop)`;
    } else {
      selectorSlides = `${selectors.slide}.desktop, ${selectors.slide}:not(.mobile)`;
    }

    this.flkty.options.cellSelector = selectorSlides;
    this.flkty.selectCell(0, false, true);
    this.flkty.reloadCells();
    this.flkty.reposition();
    this.flkty.resize();
    this.slideActions();
  }

  resizeEvents() {
    this.setMinHeight();

    if (this.multipleSlides) {
      if (this.sliderOptions.prevNextButtons) {
        this.initArrows();
      }
      this.flkty.resize();
      if (!this.slideshow.classList.contains(classes.sliderInitialized)) {
        this.flkty.select(0);
      }
    }
  }

  slideActions(changeEvent = false) {
    const currentSlide = this.slideshow.querySelector(`.${classes.isSelected}`);
    const currentSlideTextColor = currentSlide.getAttribute(attributes.slideTextColor);
    const currentSlideLink = currentSlide.querySelector(selectors.link);
    const buttons = this.slideshow.querySelectorAll(`${selectors.slide} a, ${selectors.slide} button`);

    if (currentSlideLink && this.sliderOptions.groupCells && changeEvent) {
      currentSlideLink.focus();
    }

    if (buttons.length) {
      buttons.forEach((button) => {
        const slide = button.closest(selectors.slide);
        if (slide) {
          const tabIndex = slide.classList.contains(classes.isSelected) ? 0 : -1;
          button.setAttribute('tabindex', tabIndex);
        }
      });
    }

    if (currentSlideTextColor !== 'rgba(0,0,0,0)' && currentSlideTextColor !== '') {
      this.slideshow.style.setProperty('--text', currentSlideTextColor);
    }

    this.setMinHeight();

    if (this.sliderAnimate) {
      let animatedItems = this.slideshow.querySelectorAll(`.${classes.isSelected} .${classes.aosAnimate}`);
      if (this.sliderAnimateOnce) {
        animatedItems = this.slideshow.querySelectorAll(`.${classes.isSelected}:not(.${classes.aosAnimated}) .${classes.aosAnimate}`);
      }
      if (animatedItems.length) {
        animatedItems.forEach((animatedItem) => {
          animatedItem.classList.remove(classes.aosAnimate);
          if (this.sliderAnimateOnce) {
            animatedItem.classList.add(classes.aosAnimated);
          }
        });
      }
    }

    if (this.sliderThumbs.length && this.sliderThumbs.length === this.slideshowSlides.length && currentSlide.hasAttribute(attributes.slideIndex)) {
      const slideIndex = parseInt(currentSlide.getAttribute(attributes.slideIndex));
      const currentThumb = this.container.querySelector(`${selectors.sliderThumb}.${classes.isSelected}`);
      if (currentThumb) {
        currentThumb.classList.remove(classes.isSelected);
      }
      this.sliderThumbs[slideIndex].classList.add(classes.isSelected);
    }
  }

  setMinHeight() {
    if (!this.setMinHeightFlag) return;

    this.slideshowSlides.forEach((element) => {
      const slideImageImg = element.querySelector(selectors.slideshowSlideImg);
      let slideAspectRatio = '';
      if (slideImageImg && slideImageImg.hasAttribute(attributes.aspectRatio)) {
        slideAspectRatio = slideImageImg.getAttribute(attributes.aspectRatio);
      }

      let slideTextContentHeight = 0;
      let getMargin = 0;
      const slideTextContent = element.querySelector(selectors.heroContent);
      if (slideTextContent) {
        const getMarginTop = parseInt(window.getComputedStyle(slideTextContent).marginTop);
        const getMarginBottom = parseInt(window.getComputedStyle(slideTextContent).marginBottom);
        getMargin = getMarginTop + getMarginBottom;
        slideTextContentHeight = slideTextContent.offsetHeight + getMargin;
      }

      const slideWidth = parseInt(getComputedStyle(element, null).width.replace('px', ''));
      let slideHeight = parseInt(slideWidth / slideAspectRatio) || 0;
      const isCurrentSlide = element.classList.contains(classes.isSelected);

      if (slideTextContentHeight > slideHeight) {
        slideHeight = slideTextContentHeight;
      }

      const minHeightValue = `calc(${slideHeight}px + var(--header-padding))`;
      element.style.setProperty('min-height', minHeightValue);
      const heroContentWrapper = element.querySelector(selectors.heroContentWrapper);
      if (heroContentWrapper) {
        heroContentWrapper.style.setProperty('min-height', minHeightValue);
      }
      if (isCurrentSlide) {
        this.slideshow.parentElement.style.setProperty('min-height', minHeightValue);
      }
    });
  }

  positionArrows() {
    if (this.slideshow.hasAttribute(attributes.arrowPositionMiddle) && this.sliderOptions.prevNextButtons) {
      const itemImage = this.slideshow.querySelector(selectors.collectionImage) || this.slideshow.querySelector(selectors.productItemImage) || this.slideshow.querySelector(selectors.columnImage);

      // Prevent 'clientHeight' of null error if no image
      if (!itemImage) return;

      this.slideshow.querySelector(selectors.flickityPrevArrow).style.top = itemImage.clientHeight / 2 + 'px';
      this.slideshow.querySelector(selectors.flickityNextArrow).style.top = itemImage.clientHeight / 2 + 'px';
    }
  }

  initArrows() {
    if (!this.multipleSlides) return;
    const slidesNumberCustom = parseInt(this.slideshow.getAttribute(attributes.slidesLargeDesktop));
    const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    const desktopSlides = this.slideshow.hasAttribute(attributes.slidesDesktop) ? parseInt(this.slideshow.getAttribute(attributes.slidesDesktop)) : 3;
    const tabletSlides = this.slideshow.hasAttribute(attributes.slidesTabletDesktop) ? parseInt(this.slideshow.getAttribute(attributes.slidesTabletDesktop)) : 2;
    const mobileSlides = this.slideshow.hasAttribute(attributes.slidesMobileDesktop) ? parseInt(this.slideshow.getAttribute(attributes.slidesMobileDesktop)) : 1;
    const largeDesktopCheck = windowWidth > 1339 && this.slideshowSlides.length > slidesNumberCustom;
    const desktopCheck = windowWidth <= 1339 && windowWidth > 1023 && this.slideshowSlides.length > desktopSlides;
    const tabletCheck = windowWidth <= 1023 && windowWidth > 749 && this.slideshowSlides.length > tabletSlides;
    const mobileCheck = windowWidth <= 749 && this.slideshowSlides.length > mobileSlides;
    const flag = Boolean(largeDesktopCheck || desktopCheck || tabletCheck || mobileCheck);
    this.slideshow.classList.toggle(classes.sliderArrowsHidden, !flag);
    this.slideshow.classList.toggle(classes.sliderInitialized, flag);
  }

  equalizeHeight() {
    Flickity.prototype._createResizeClass = function () {
      setTimeout(() => {
        this.element.classList.add(classes.flickityResize);
      });
    };

    Flickity.createMethods.push('_createResizeClass');

    const resize = Flickity.prototype.resize;
    Flickity.prototype.resize = function () {
      this.element.classList.remove(classes.flickityResize);
      resize.call(this);
      this.element.classList.add(classes.flickityResize);
    };

    this.flkty.resize();
  }

  onUnload() {
    if (this.setMinHeightFlag || this.multipleSlides) {
      window.removeEventListener('resize', this.resizeEvent);
    }

    if (this.slideshow && this.flkty) {
      this.flkty.options.watchCSS = false;
      this.flkty.destroy();
    }

    this.container.removeEventListener('theme:tab:change', this.resetSliderEvent);
  }

  onBlockSelect(evt) {
    if (!this.slideshow) return;
    // Ignore the cloned version
    const slide = this.slideshow.querySelector(`[${selectors.slideValue}="${evt.detail.blockId}"]`);

    if (!slide) return;
    let slideIndex = parseInt(slide.getAttribute(attributes.slideIndex));

    if (this.multipleSlides && !this.slideshow.classList.contains(classes.sliderInitialized)) {
      slideIndex = 0;
    }

    this.slideshow.classList.add(classes.isSelected);

    // Go to selected slide, pause autoplay
    this.flkty.selectCell(slideIndex);
    this.flkty.stopPlayer();
  }

  onBlockDeselect() {
    if (!this.slideshow) return;
    this.slideshow.classList.remove(classes.isSelected);

    if (!this.sliderOptions.hasOwnProperty('autoPlay')) return;
    this.flkty.playPlayer();
  }
}

const slider = {
  onLoad() {
    sections[this.id] = [];
    const els = this.container.querySelectorAll(selectors.slider);
    els.forEach((el) => {
      sections[this.id].push(new Slider(this.container, el));
    });
  },
  onUnload() {
    sections[this.id].forEach((el) => {
      if (typeof el.onUnload === 'function') {
        el.onUnload();
      }
    });
  },
  onBlockSelect(e) {
    sections[this.id].forEach((el) => {
      if (typeof el.onBlockSelect === 'function') {
        el.onBlockSelect(e);
      }
    });
  },
  onBlockDeselect(e) {
    sections[this.id].forEach((el) => {
      if (typeof el.onBlockDeselect === 'function') {
        el.onBlockDeselect(e);
      }
    });
  },
};

export {slider, Slider};
